import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminAiDatasetDetail,
  type AdminAiSplit,
} from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Input,
  Modal,
  NUMERIC,
  Select,
  Textarea,
} from "pages/admin/ui";
import {
  ACTION_LABEL,
  FEATURES,
  LABEL_HELP,
  LABEL_TEXT,
  LABEL_TONE,
  SPLITS,
  answerOf,
  answerSourceOf,
  dpoPairs,
  editRatioLabel,
  fieldLabel,
  formatOccurredAt,
  commentMeta,
  inputLineItems,
  outputFieldsOf,
  parseTrainingConfig,
  speakerOf,
  thoughtRecordSections,
  serializeTrainingConfig,
  sftSample,
  toAdminOutput,
  type TrainingConfig,
} from "./aiFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const errorMessageOf = (e: unknown) =>
  (e as AxiosError).response?.status === 403
    ? "권한이 없는 계정이에요"
    : FALLBACK_ERROR_MESSAGE;

type TrainingTab = "sft" | "dpo" | "rl";

const TRAINING_TABS: { key: TrainingTab; label: string; help: string }[] = [
  { key: "sft", label: "SFT", help: "입력 → 정답을 그대로 따라 배우는 학습" },
  {
    key: "dpo",
    label: "DPO",
    help: "같은 입력에서 더 나은 답(chosen)과 덜 나은 답(rejected)을 대비해 배우는 학습",
  },
  {
    key: "rl",
    label: "RL",
    help: "모델이 답을 만들고 보상 함수 점수로 나아지는 학습. 정답은 reference 로만 쓴다",
  },
];

const ENABLED_KEY = {
  sft: "sftEnabled",
  dpo: "dpoEnabled",
  rl: "rlEnabled",
} as const;

const ANSWER_SOURCE = {
  admin: { text: "관리자 보완", tone: "info" },
  coach: { text: "코치 값", tone: "ok" },
  none: { text: "없음 · 샘플 안 만들어짐", tone: "warn" },
} as const;

// 모달 폼 상태 — 저장 단위와 같다 (서버 dataset/update 는 폼 전체를 받는다)
type FormState = {
  adminDraft: Record<string, string>;
  sftEnabled: boolean;
  dpoEnabled: boolean;
  rlEnabled: boolean;
  split: AdminAiSplit;
  config: TrainingConfig;
  memo: string;
};

const formOf = (detail: AdminAiDatasetDetail): FormState => ({
  adminDraft: Object.fromEntries(
    Object.entries(detail.adminOutput ?? {}).map(([k, v]) => [k, v ?? ""])
  ),
  sftEnabled: detail.sftEnabled,
  dpoEnabled: detail.dpoEnabled,
  rlEnabled: detail.rlEnabled,
  split: detail.split,
  config: parseTrainingConfig(detail.feature, detail.trainingConfig),
  memo: detail.memo ?? "",
});

/**
 * 데이터셋 행 상세 — 왼쪽은 대화 맥락(입력 스냅샷), 오른쪽은 정답 만들기(AI 초안 → 코치 최종 → 학습 정답)와 학습 사용.
 * 보완 칸을 비우면 코치 값이 학습 정답이다. 적재된 원본 값은 바꾸지 않는다.
 * 학습 세부 설정(분할·프롬프트 버전·DPO·RL·샘플 미리보기)은 드물게 바꾸므로 접어 둔다. 메모도 필요할 때만 연다.
 */
export default function DatasetRowModal({
  id,
  rowIds,
  onNavigate,
  onSaved,
  onClose,
}: {
  id: number;
  // 목록의 현재 페이지 행 순서 — 이전/다음 이동용
  rowIds: number[];
  onNavigate: (id: number) => void;
  onSaved: (detail: AdminAiDatasetDetail) => void;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AdminAiDatasetDetail | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TrainingTab>("sft");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [memoOpen, setMemoOpen] = useState(false);
  // 자동적 사고 — 코치가 AI 초안과 다르게 쓴 항목만 펼친다
  const [onlyChanged, setOnlyChanged] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    setMemoOpen(false);
    (async () => {
      try {
        const res = await adminApi.aiDatasetDetail(id);
        if (cancelled) return;
        if (res.code === 0 && res.result) {
          setDetail(res.result);
          setForm(formOf(res.result));
          setInitialForm(formOf(res.result));
        } else {
          setError(res.message || FALLBACK_ERROR_MESSAGE);
        }
      } catch (e) {
        if (cancelled) return;
        setError(errorMessageOf(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // 보완 칸은 비운 칸을 빼고 비교한다 — 입력했다 지운 칸({k: ""})을 변경으로 보지 않게
  const normalized = (f: FormState | null) =>
    f &&
    JSON.stringify({
      ...f,
      adminDraft: toAdminOutput(f.adminDraft),
      memo: f.memo.trim(),
    });
  const dirty = normalized(form) !== normalized(initialForm);

  // 저장하지 않은 변경이 있으면 떠나기 전에 묻는다.
  // 저장 중에는 떠나지 않는다 — 응답이 다른 행을 연 뒤에 도착하면 그 행 화면에 이 행 값이 덮인다
  const leave = useCallback(
    (next: () => void) => {
      if (saving) return;
      if (dirty && !window.confirm("저장하지 않은 변경이 있어요. 나갈까요?"))
        return;
      next();
    },
    [dirty, saving]
  );
  const close = useCallback(() => leave(onClose), [leave, onClose]);

  const index = rowIds.indexOf(id);
  const prevId = index > 0 ? rowIds[index - 1] : undefined;
  const nextId =
    index >= 0 && index < rowIds.length - 1 ? rowIds[index + 1] : undefined;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  const updateConfig = (next: Partial<TrainingConfig>) =>
    setForm((prev) =>
      prev ? { ...prev, config: { ...prev.config, ...next } } : prev
    );

  const save = async (): Promise<boolean> => {
    if (!detail || !form) return false;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.aiDatasetUpdate({
        id: detail.id,
        adminOutput: toAdminOutput(form.adminDraft),
        sftEnabled: form.sftEnabled,
        dpoEnabled: form.dpoEnabled,
        rlEnabled: form.rlEnabled,
        split: form.split,
        trainingConfig: serializeTrainingConfig(form.config),
        memo: form.memo.trim() || null,
      });
      if (res.code === 0 && res.result) {
        setDetail(res.result);
        setForm(formOf(res.result));
        setInitialForm(formOf(res.result));
        onSaved(res.result);
        return true;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
      return false;
    } catch (e) {
      setError(errorMessageOf(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // 검수 흐름의 기본 동작 — 바뀐 게 있으면 저장하고 다음 행으로. 마지막 행이면 저장만 한다
  const saveAndNext = async () => {
    if (saving || !form) return;
    if (dirty && !(await save())) return;
    if (nextId !== undefined) onNavigate(nextId);
  };
  const canSaveAndNext = !!form && (dirty || nextId !== undefined);

  // ⌘/Ctrl + Enter = 저장 후 다음
  const saveAndNextRef = useRef(saveAndNext);
  saveAndNextRef.current = saveAndNext;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        saveAndNextRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const adminOutput = form ? toAdminOutput(form.adminDraft) : null;
  const sample = useMemo(
    () =>
      detail && form
        ? sftSample(detail, adminOutput, form.config.sft.systemPromptVersion)
        : null,
    // adminOutput 은 form 에서 파생된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, form]
  );
  const pairs = useMemo(
    () =>
      detail && form ? dpoPairs(detail, adminOutput, form.config.dpo) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, form]
  );

  const featureLabel =
    FEATURES.find((f) => f.key === detail?.feature)?.label ?? "";
  const source = detail
    ? ANSWER_SOURCE[answerSourceOf(detail.coachOutput, adminOutput)]
    : null;

  return (
    <Modal
      title={`행 #${id}`}
      subject={
        detail ? (
          <span className="flex flex-wrap items-center gap-1.5 whitespace-normal">
            <Badge tone="info">{featureLabel}</Badge>
            <Badge tone="idle">
              {ACTION_LABEL[detail.action]}
              {detail.field ? ` · ${fieldLabel(detail.field)}` : ""}
            </Badge>
            <span title={LABEL_HELP} className="cursor-help">
              <Badge tone={LABEL_TONE[detail.label]}>
                라벨 · {LABEL_TEXT[detail.label]}
                {detail.editRatio != null && detail.label === "edited"
                  ? ` · ${editRatioLabel(detail.editRatio)}`
                  : ""}
              </Badge>
            </span>
            <Badge tone="idle">분할 {form?.split ?? detail.split}</Badge>
            <span className={`text-slate-500 ${NUMERIC}`}>
              · {detail.coachName ?? "코치 미상"} · 내담자 #{detail.patientId} ·{" "}
              {formatOccurredAt(detail.occurredAt)}
            </span>
          </span>
        ) : undefined
      }
      // 대화와 정답을 나란히 보는 작업 화면이라 거의 전체 화면으로 연다 (오버레이 여백은 남긴다)
      width="96vw"
      height="96vh"
      onClose={close}
      footer={
        <>
          {/* 메모는 필요할 때만 연다 — 이미 있으면 처음부터 보인다 */}
          <div className="mr-auto flex min-w-0 flex-1 items-center gap-3">
            {form && (memoOpen || form.memo) ? (
              <Input
                fullWidth={false}
                className="w-full max-w-md"
                aria-label="메모"
                placeholder="메모 (선택)"
                maxLength={1000}
                value={form.memo}
                onChange={(e) => update("memo", e.target.value)}
              />
            ) : (
              <button
                type="button"
                disabled={!form}
                onClick={() => setMemoOpen(true)}
                className="text-sm font-medium text-[#0F55A7] hover:underline disabled:opacity-40"
              >
                + 메모
              </button>
            )}
            <span className="shrink-0 text-xs text-slate-500">
              {detail?.adminUpdatedBy
                ? `마지막 수정 ${detail.adminUpdatedBy} · ${formatOccurredAt(detail.adminUpdatedAt ?? "")}`
                : "관리자 수정 없음"}
            </span>
          </div>
          <Button
            disabled={prevId === undefined || saving}
            onClick={() =>
              prevId !== undefined && leave(() => onNavigate(prevId))
            }
          >
            이전 행
          </Button>
          <Button
            disabled={nextId === undefined || saving}
            onClick={() =>
              nextId !== undefined && leave(() => onNavigate(nextId))
            }
          >
            다음 행
          </Button>
          <Button disabled={!dirty || !form || saving} onClick={save}>
            저장
          </Button>
          <Button
            variant="primary"
            disabled={!canSaveAndNext}
            loading={saving}
            loadingLabel="저장 중"
            onClick={saveAndNext}
            title="⌘/Ctrl + Enter"
          >
            {nextId === undefined ? "저장" : "저장 후 다음"}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {!detail || !form ? (
        !error && (
          <div className="flex flex-col gap-2 py-6">
            <span className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
            <span className="h-40 w-full animate-pulse rounded bg-slate-100" />
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:h-full lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)]">
          {/* 왼쪽 — 대화 맥락 (이 칸만 따로 스크롤) */}
          <div className="flex min-w-0 flex-col gap-3 lg:min-h-0">
            <ColumnTitle
              title="대화 맥락"
              sub="입력"
              note={
                detail.feature === "thought_record_comment"
                  ? "생각기록지 · 코멘트 위치 강조"
                  : detail.feature === "automatic_thought"
                    ? "생각기록지 · 항목별 기록과 코치 코멘트"
                    : "적재 시점 스냅샷 · 최신 메시지가 아래"
              }
            />
            <ContextView detail={detail} />

            {detail.groupRows.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-semibold text-slate-500">
                  같은 원본의 행
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.groupRows.map((g) => (
                    <button
                      type="button"
                      key={g.id}
                      disabled={g.id === detail.id || saving}
                      onClick={() => leave(() => onNavigate(g.id))}
                      className={`rounded-md border px-2 py-1 text-xs ${
                        g.id === detail.id
                          ? "border-[#0F55A7]/30 bg-[#DCEFFA] text-[#0F55A7]"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      #{g.id} {ACTION_LABEL[g.action]}
                      {g.field ? ` · ${fieldLabel(g.field)}` : ""} ·{" "}
                      {LABEL_TEXT[g.label]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 오른쪽 — 정답 만들기 + 학습 사용 */}
          <div className="flex min-w-0 flex-col gap-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            <ColumnTitle
              title="정답 만들기"
              note="보완 칸을 비우면 코치 값이 정답"
            />

            {outputFieldsOf(detail.feature).length === 1 ? (
              (() => {
                const key = outputFieldsOf(detail.feature)[0].key;
                const ai = detail.aiOutput?.[key] ?? "";
                const coach =
                  detail.coachOutput == null
                    ? null
                    : (detail.coachOutput[key] ?? "");
                const draft = form.adminDraft[key] ?? "";
                const setDraft = (value: string) =>
                  update("adminDraft", { ...form.adminDraft, [key]: value });
                return (
                  <>
                    <SourceCard
                      title="AI 초안"
                      onStart={ai ? () => setDraft(ai) : undefined}
                    >
                      {ai || (
                        <span className="text-slate-400">(비어 있음)</span>
                      )}
                    </SourceCard>
                    <SourceCard
                      title="코치 최종"
                      badge={
                        coach == null ? (
                          <Badge tone="idle">안 씀</Badge>
                        ) : (
                          <Badge tone={LABEL_TONE[detail.label]}>
                            {LABEL_TEXT[detail.label]}
                          </Badge>
                        )
                      }
                      onStart={coach ? () => setDraft(coach) : undefined}
                    >
                      {coach == null ? (
                        <span className="text-slate-400">
                          코치가 이 AI 초안을 쓰지 않았어요.
                        </span>
                      ) : (
                        coach || <span className="text-slate-400">(비움)</span>
                      )}
                    </SourceCard>

                    {/* 학습 정답 — 이 칸이 학습에 쓰인다 */}
                    <div className="rounded-lg border-2 border-[#0F55A7] bg-white">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 pb-1 pt-2.5">
                        <span className="text-sm font-semibold text-[#0F55A7]">
                          학습 정답
                        </span>
                        {source && (
                          <Badge tone={source.tone}>
                            정답 출처 · {source.text}
                          </Badge>
                        )}
                      </div>
                      <textarea
                        aria-label="학습 정답"
                        className="block min-h-[12rem] w-full resize-y border-0 bg-transparent px-3 pb-3 pt-1.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none"
                        placeholder={
                          coach
                            ? "비워 두면 코치 최종이 정답이에요. 고치려면 [이걸로 시작]을 누르거나 직접 쓰세요."
                            : "코치가 쓰지 않은 행이에요. 여기에 정답을 쓰면 학습 샘플이 만들어져요."
                        }
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                      />
                      <div className="flex items-center justify-between border-t border-dashed border-slate-200 px-3 py-1.5 text-xs text-slate-500">
                        <span className={NUMERIC}>{draft.length}자</span>
                        <button
                          type="button"
                          disabled={!draft}
                          onClick={() => setDraft("")}
                          className="font-medium text-[#0F55A7] hover:underline disabled:opacity-40"
                        >
                          비우기 (코치 값으로)
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              <FieldAnswers
                detail={detail}
                draft={form.adminDraft}
                onChange={(next) => update("adminDraft", next)}
                onlyChanged={onlyChanged}
                onOnlyChanged={setOnlyChanged}
                sourceBadge={
                  source && (
                    <Badge tone={source.tone}>정답 출처 · {source.text}</Badge>
                  )
                }
              />
            )}

            {/* 학습 사용 — 켜고 끄기만 한 줄로, 세부 설정은 접어 둔다 */}
            <div className="rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-center gap-4 px-3 py-2.5">
                <span className="text-xs font-semibold text-slate-500">
                  학습 사용
                </span>
                {TRAINING_TABS.map(({ key, label, help }) => (
                  <UseCheck
                    key={key}
                    label={label}
                    help={help}
                    checked={form[ENABLED_KEY[key]]}
                    onChange={(v) => update(ENABLED_KEY[key], v)}
                  />
                ))}
                <button
                  type="button"
                  aria-expanded={settingsOpen}
                  onClick={() => setSettingsOpen((v) => !v)}
                  className="ml-auto text-xs font-semibold text-[#0F55A7] hover:underline"
                >
                  {settingsOpen ? "▾ 설정 접기" : "▸ 설정"}
                </button>
              </div>

              {settingsOpen && (
                <div className="flex flex-col gap-4 border-t border-slate-200 bg-white px-3 pb-4 pt-3">
                  <div className="flex gap-1 border-b border-slate-200">
                    {TRAINING_TABS.map(({ key, label }) => (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setTab(key)}
                        className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm ${
                          tab === key
                            ? "border-[#0F55A7] font-semibold text-[#0F55A7]"
                            : "border-transparent text-slate-500 hover:text-slate-900"
                        }`}
                      >
                        {label}
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${form[ENABLED_KEY[key]] ? "bg-emerald-500" : "bg-slate-300"}`}
                          aria-label={
                            form[ENABLED_KEY[key]] ? "사용" : "사용 안 함"
                          }
                        />
                      </button>
                    ))}
                  </div>
                  {tab === "sft" && (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 gap-3">
                        <Field
                          label="분할"
                          helper="내담자 단위 자동 배정. 바꾸면 이 행만 바뀐다"
                        >
                          <Select
                            value={form.split}
                            onChange={(e) =>
                              update("split", e.target.value as AdminAiSplit)
                            }
                          >
                            {SPLITS.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Field label="system 프롬프트 버전">
                          <Input
                            value={form.config.sft.systemPromptVersion}
                            onChange={(e) =>
                              updateConfig({
                                sft: { systemPromptVersion: e.target.value },
                              })
                            }
                          />
                        </Field>
                      </div>
                      <Preview title="만들어지는 샘플 (messages)">
                        {sample
                          ? JSON.stringify(sample, null, 2)
                          : "정답이 비어 있어 샘플이 만들어지지 않아요 (안 씀 행은 관리자 보완으로 정답을 채우면 쓸 수 있어요)"}
                      </Preview>
                    </div>
                  )}

                  {tab === "dpo" && (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="chosen (선택된 답)">
                          <Input value="정답 (코치 + 관리자 보완)" disabled />
                        </Field>
                        <Field label="rejected (거부된 답)">
                          <Select
                            value={form.config.dpo.rejected}
                            onChange={(e) =>
                              updateConfig({
                                dpo: {
                                  ...form.config.dpo,
                                  rejected: e.target.value as "ai" | "custom",
                                },
                              })
                            }
                          >
                            <option value="ai">AI 초안</option>
                            <option value="custom">직접 입력</option>
                          </Select>
                        </Field>
                      </div>
                      {form.config.dpo.rejected === "custom" && (
                        <Field label="rejected 직접 입력">
                          <Textarea
                            rows={3}
                            value={form.config.dpo.rejectedCustom}
                            onChange={(e) =>
                              updateConfig({
                                dpo: {
                                  ...form.config.dpo,
                                  rejectedCustom: e.target.value,
                                },
                              })
                            }
                          />
                        </Field>
                      )}
                      {detail.feature === "automatic_thought" &&
                        form.config.dpo.rejected === "ai" && (
                          <Field label="쌍 단위">
                            <Select
                              value={form.config.dpo.pairUnit}
                              onChange={(e) =>
                                updateConfig({
                                  dpo: {
                                    ...form.config.dpo,
                                    pairUnit: e.target.value as
                                      | "whole"
                                      | "field",
                                  },
                                })
                              }
                            >
                              <option value="whole">전체 한 쌍</option>
                              <option value="field">바뀐 항목마다 한 쌍</option>
                            </Select>
                          </Field>
                        )}
                      <div className="flex flex-col gap-2">
                        <p className="text-xs font-medium text-slate-600">
                          만들어지는 쌍 {pairs.length}개
                        </p>
                        {pairs.length === 0 ? (
                          <p className="text-sm text-slate-400">
                            정답과 rejected 가 같거나 비어 있어 쌍이 없어요
                          </p>
                        ) : (
                          pairs.map((p) => (
                            <div
                              key={p.title}
                              className="rounded-lg border border-slate-200 p-3 text-sm"
                            >
                              <p className="text-xs font-semibold text-slate-500">
                                {p.title}
                              </p>
                              <p className="mt-1.5 whitespace-pre-wrap">
                                <span className="mr-1.5 text-xs font-medium text-emerald-700">
                                  chosen
                                </span>
                                {p.chosen}
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-slate-500">
                                <span className="mr-1.5 text-xs font-medium text-rose-600">
                                  rejected
                                </span>
                                {p.rejected || "(비어 있음)"}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {tab === "rl" && (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="쓰임">
                          <Select
                            value={form.config.rl.usage}
                            onChange={(e) =>
                              updateConfig({
                                rl: {
                                  ...form.config.rl,
                                  usage: e.target.value as "train" | "eval",
                                },
                              })
                            }
                          >
                            <option value="train">학습 프롬프트 풀</option>
                            <option value="eval">평가셋 (학습에 안 씀)</option>
                          </Select>
                        </Field>
                        <Field label="채점 방식">
                          <Select
                            value={form.config.rl.grading}
                            onChange={(e) =>
                              updateConfig({
                                rl: {
                                  ...form.config.rl,
                                  grading: e.target
                                    .value as TrainingConfig["rl"]["grading"],
                                },
                              })
                            }
                          >
                            <option value="mixed">규칙 + LLM 심사 혼합</option>
                            <option value="rule">규칙만</option>
                            <option value="llm">LLM 심사만</option>
                          </Select>
                        </Field>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                              <th className="w-12 px-3 py-2 font-semibold">
                                사용
                              </th>
                              <th className="px-3 py-2 font-semibold">
                                보상 기준 (reward)
                              </th>
                              <th className="w-20 px-3 py-2 text-right font-semibold">
                                가중치
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {form.config.rl.rewards.map((r, i) => {
                              const setReward = (next: Partial<typeof r>) =>
                                updateConfig({
                                  rl: {
                                    ...form.config.rl,
                                    rewards: form.config.rl.rewards.map(
                                      (x, j) =>
                                        j === i ? { ...x, ...next } : x
                                    ),
                                  },
                                });
                              return (
                                <tr
                                  key={r.key}
                                  className="border-b border-slate-100 align-top last:border-b-0"
                                >
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      aria-label={`${r.label} 사용`}
                                      checked={r.on}
                                      onChange={(e) =>
                                        setReward({ on: e.target.checked })
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <p className="text-slate-900">{r.label}</p>
                                    <p className="text-xs text-slate-500">
                                      {r.how}
                                    </p>
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      sizing="sm"
                                      min={0}
                                      step={0.5}
                                      aria-label={`${r.label} 가중치`}
                                      className={`text-right ${NUMERIC}`}
                                      value={r.weight}
                                      disabled={!r.on}
                                      onChange={(e) =>
                                        setReward({
                                          weight: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <Field label="LLM 심사 루브릭 메모">
                        <Textarea
                          rows={3}
                          value={form.config.rl.rubric}
                          onChange={(e) =>
                            updateConfig({
                              rl: { ...form.config.rl, rubric: e.target.value },
                            })
                          }
                        />
                      </Field>
                      <Preview title="reference (정답)">
                        {JSON.stringify(
                          answerOf(detail.coachOutput, adminOutput),
                          null,
                          2
                        )}
                      </Preview>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ColumnTitle({
  title,
  sub,
  note,
}: {
  title: string;
  sub?: string;
  note?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-sm font-semibold text-slate-900">
        {title}
        {sub && (
          <span className="ml-1.5 font-normal text-slate-400">{sub}</span>
        )}
      </p>
      {note && <p className="truncate text-xs text-slate-400">{note}</p>}
    </div>
  );
}

// 정답 만들기의 출처 카드 (AI 초안 · 코치 최종) — [이걸로 시작] 을 누르면 그 값이 학습 정답 칸에 복사된다
function SourceCard({
  title,
  badge,
  onStart,
  children,
}: {
  title: string;
  badge?: ReactNode;
  onStart?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
          {title}
          {badge}
        </span>
        <button
          type="button"
          disabled={!onStart}
          onClick={onStart}
          className="rounded-md border border-slate-300 px-2.5 py-0.5 text-xs text-slate-600 hover:border-[#0F55A7] hover:text-[#0F55A7] disabled:opacity-40 disabled:hover:border-slate-300 disabled:hover:text-slate-600"
        >
          이걸로 시작
        </button>
      </div>
      <div className="whitespace-pre-wrap break-words px-3 py-2.5 text-sm leading-relaxed text-slate-700">
        {children}
      </div>
    </div>
  );
}

// 자동적 사고 8개 항목 — 항목마다 AI 초안 · 코치 최종 · 학습 정답(보완)을 한 줄로. 달라진 항목만 펼쳐 둔다
function FieldAnswers({
  detail,
  draft,
  onChange,
  onlyChanged,
  onOnlyChanged,
  sourceBadge,
}: {
  detail: AdminAiDatasetDetail;
  draft: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  onlyChanged: boolean;
  onOnlyChanged: (next: boolean) => void;
  sourceBadge: ReactNode;
}) {
  const fields = outputFieldsOf(detail.feature);
  const coachOf = (key: string) =>
    detail.coachOutput == null ? null : (detail.coachOutput[key] ?? "");
  const changedCount = fields.filter(
    ({ key }) => (detail.aiOutput?.[key] ?? "") !== (coachOf(key) ?? "")
  ).length;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone={changedCount ? "info" : "ok"}>
          {changedCount}/{fields.length} 항목 수정
        </Badge>
        {sourceBadge}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyChanged}
            onChange={(e) => onOnlyChanged(e.target.checked)}
          />
          달라진 항목만 펼치기
        </label>
      </div>
      {fields.map(({ key, label }) => {
        const ai = detail.aiOutput?.[key] ?? "";
        const coach = coachOf(key);
        const same = ai === (coach ?? "");
        const fieldLabelValue = detail.fieldLabels?.[key];
        return (
          // key 에 onlyChanged 를 넣어 체크를 바꾸면 펼침 상태를 다시 맞춘다
          <details
            key={`${key}-${onlyChanged}`}
            open={!same || !onlyChanged}
            className="group rounded-lg border border-slate-200 bg-white"
          >
            <summary className="grid cursor-pointer list-none grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2 group-open:border-b group-open:border-slate-200 [&::-webkit-details-marker]:hidden">
              <span className="text-sm font-semibold text-slate-800">
                {label}
              </span>
              <span className="truncate text-sm text-slate-500">
                {(draft[key] ?? "").trim() || coach || "–"}
              </span>
              {fieldLabelValue && (
                <Badge tone={LABEL_TONE[fieldLabelValue]}>
                  {LABEL_TEXT[fieldLabelValue]}
                </Badge>
              )}
            </summary>
            <div className="grid grid-cols-1 gap-3 px-3 pb-3 pt-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)]">
              <div>
                <p className="mb-0.5 text-[11px] font-semibold text-slate-400">
                  AI 초안
                </p>
                <p className="whitespace-pre-wrap break-words text-sm text-slate-600">
                  {ai || <span className="text-slate-300">(비어 있음)</span>}
                </p>
              </div>
              <div>
                <p className="mb-0.5 text-[11px] font-semibold text-slate-400">
                  코치 최종
                </p>
                <p className="whitespace-pre-wrap break-words text-sm text-slate-900">
                  {coach == null ? (
                    <span className="text-slate-300">(안 씀)</span>
                  ) : (
                    coach || <span className="text-slate-300">–</span>
                  )}
                </p>
              </div>
              <div>
                <p className="mb-0.5 text-[11px] font-semibold text-[#0F55A7]">
                  학습 정답
                </p>
                <Textarea
                  rows={3}
                  aria-label={`${label} 학습 정답`}
                  className="border-[#0F55A7]/60"
                  placeholder={coach ? `비우면 코치 값: ${coach}` : ""}
                  value={draft[key] ?? ""}
                  onChange={(e) =>
                    onChange({ ...draft, [key]: e.target.value })
                  }
                />
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );
}

// 학습 사용 한 줄에 들어가는 체크박스
function UseCheck({
  label,
  help,
  checked,
  onChange,
}: {
  label: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      title={help}
      className="flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-slate-700"
    >
      <input
        type="checkbox"
        className="h-4 w-4 accent-[#0F55A7]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function Preview({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-slate-600">{title}</p>
      <pre className="whitespace-pre-wrap break-all rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
        {children}
      </pre>
    </div>
  );
}

// 대화 맥락 — 채팅은 말풍선(내담자 왼쪽 · 상담사 오른쪽, 열리면 맨 아래로), 자동적 사고는 생각기록지 항목(상황·생각·감정·신체 반응·행동, 위에서부터),
// 코멘트는 생각기록지 줄 목록(코멘트 위치 줄 강조, 그 줄로 스크롤) + 원문 JSON(접어 둠)
function ContextView({ detail }: { detail: AdminAiDatasetDetail }) {
  const lines = inputLineItems(detail.feature, detail.input);
  const raw = detail.input?.thought_record_data;
  const isComment = detail.feature === "thought_record_comment";
  // 자동적 사고 입력은 대화가 아니라 생각기록지(항목별 내담자 기록 + 코치 코멘트)다 — 위에서부터 읽는다
  const isActivity = detail.feature === "automatic_thought";
  const meta = isComment ? commentMeta(detail.input) : null;
  const boxRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || isActivity) return;
    const target = targetRef.current;
    if (target) {
      box.scrollTop = target.offsetTop - box.offsetTop - box.clientHeight / 2;
    } else {
      box.scrollTop = box.scrollHeight;
    }
  }, [detail.id, isActivity]);

  const messages = lines.map((line) => ({ ...line, ...speakerOf(line.text) }));
  const lastPatient = messages.map((m) => m.who).lastIndexOf("patient");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {meta && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <span className="text-slate-500">
            코멘트 위치{" "}
            <span className="text-slate-900">{meta.locationText ?? "–"}</span>
          </span>
          {meta.commentType && (
            <span className="text-slate-500">
              유형 <span className="text-slate-900">{meta.commentType}</span>
            </span>
          )}
        </div>
      )}
      {lines.length === 0 ? (
        <p className="text-sm text-slate-400">입력 스냅샷이 없어요</p>
      ) : isActivity ? (
        <div className="flex max-h-[60vh] min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 lg:max-h-none">
          {thoughtRecordSections(lines.map((line) => line.text)).map(
            (section, i) => (
              <div
                key={i}
                className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3"
              >
                <span className="pt-2 text-xs font-semibold text-slate-500">
                  {section.label}
                </span>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p
                    className={`whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed ${
                      section.entry ? "text-slate-900" : "text-slate-300"
                    }`}
                  >
                    {section.entry || "(비어 있음)"}
                  </p>
                  {section.comments.map((c, j) => (
                    <div
                      key={j}
                      className="ml-3 border-l-2 border-slate-200 pl-3 text-sm"
                    >
                      <p className="whitespace-pre-wrap break-words text-xs text-slate-500">
                        <span className="font-semibold">코치</span> {c.question}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-slate-800">
                        <span className="text-xs font-semibold text-slate-500">
                          내담자
                        </span>{" "}
                        {c.answer || (
                          <span className="text-slate-300">(답 없음)</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      ) : (
        <div
          ref={boxRef}
          className="flex max-h-[60vh] min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 lg:max-h-none"
        >
          {messages.map((m, i) =>
            m.who ? (
              <div
                key={i}
                className={`flex max-w-[85%] flex-col gap-0.5 ${
                  m.who === "patient" ? "self-start" : "items-end self-end"
                }`}
              >
                <span className="px-1 text-[11px] text-slate-400">
                  {m.who === "patient" ? "내담자" : "상담사"}
                </span>
                <div
                  className={`whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    m.who === "patient"
                      ? "rounded-tl-sm border border-slate-200 bg-white text-slate-900"
                      : "rounded-tr-sm bg-[#0F55A7] text-white"
                  } ${!isComment && i === lastPatient ? "ring-2 ring-amber-400" : ""}`}
                >
                  {m.text}
                </div>
                {!isComment && i === lastPatient && (
                  <span className="px-1 text-[11px] font-semibold text-amber-700">
                    ↑ 이번 답변이 이어질 메시지
                  </span>
                )}
              </div>
            ) : (
              <div
                key={i}
                ref={m.isTarget ? targetRef : undefined}
                className={`whitespace-pre-wrap break-words rounded-md border px-3 py-2 text-sm ${
                  m.isTarget
                    ? "border-amber-400 bg-amber-50 font-medium text-slate-900"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                {m.text.trim()}
                {m.isTarget && (
                  <span className="ml-1.5 text-xs font-semibold text-amber-700">
                    ← 코멘트 위치
                  </span>
                )}
              </div>
            )
          )}
        </div>
      )}
      {typeof raw === "string" && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">생각기록지 원문 JSON</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all rounded-lg border border-slate-200 bg-slate-50 p-3">
            {raw}
          </pre>
        </details>
      )}
    </div>
  );
}
